import { useCallback, useMemo } from 'react';
import { useAnalytics } from '@/services/analytics';

const INTEGRATION_NAME = 'Reporting Webhooks';
const INTEGRATION_CATEGORY = 'Data management';
const LIST_SOURCE = 'Webhook List Page';
/** Both the create and the edit form report themselves as the edit page. */
const FORM_SOURCE = 'Webhook Edit Page';

/** The enable/disable payloads are identical across surfaces bar the source and the enable label. */
/**
 * No endpoint id, deliberately. Ember hands one to the tracker but its `enableStarted` transform
 * drops it before Mixpanel sees it — unlike `disableStarted`, which keeps it. Adding one here would
 * put a property on `Integration: Enable Started` that the event has never carried.
 */
function enableStartedPayload(transition: 'Enabled' | 'Re-Enabled', source: string) {
  return {
    'Integration Name': INTEGRATION_NAME,
    'Integration Category': INTEGRATION_CATEGORY,
    'Integration Enabled or Re-Enabled': transition,
    Source: source,
  };
}

function disableStartedPayload(endpointId: string, isLastEnabled: boolean, source: string) {
  return {
    'Integration Name': INTEGRATION_NAME,
    'Integration Category': INTEGRATION_CATEGORY,
    'Webhook Endpoint ID': endpointId,
    'Webhook Endpoint Disabled or Deleted': 'Disabled',
    ...(isLastEnabled ? { 'Integration Disabled or Deleted': 'Disabled' } : {}),
    Source: source,
  };
}

/** Which control opened the edit page — the row link or the dropdown item. */
export type EditSource = 'Webhook Row Click' | 'Webhook Drop-down Click';

/**
 * Reporting-webhook list analytics, with the shared meta baked in.
 *
 * Two quirks are preserved deliberately, because the Mixpanel dashboards depend on them:
 *
 * - Delete fires `Integration: Disable Started`, not a delete event, distinguished only by
 *   `Webhook Endpoint Disabled or Deleted: 'Deleted'`.
 * - `Integration Disabled or Deleted` is sent *only* when the action empties the workspace —
 *   disabling the last enabled webhook, or deleting the only webhook. Otherwise it is omitted.
 *
 * Firing order also matters: disable/delete fire when the confirmation modal opens (not on
 * confirm), and enable fires before the save, so it is recorded even if the save then fails.
 */
export function useReportingWebhookAnalytics() {
  const { track } = useAnalytics();

  const trackEditStarted = useCallback(
    (endpointId: string, source: EditSource) =>
      track('integration', 'reportingWebhookEditStarted', {
        'Integration Name': INTEGRATION_NAME,
        'Integration Category': INTEGRATION_CATEGORY,
        'Webhook Endpoint ID': endpointId,
        Source: source,
      }),
    [track],
  );

  const trackKeyCopied = useCallback(
    (endpointId: string) =>
      track('integration', 'reportingWebhookKeyCopied', {
        'Webhook Endpoint ID': endpointId,
        Source: LIST_SOURCE,
      }),
    [track],
  );

  /**
   * No endpoint id, deliberately. Ember hands one to the tracker but its `enableStarted` transform
   * drops it before Mixpanel sees it — unlike `disableStarted`, which keeps it. Adding one here
   * would put a property on `Integration: Enable Started` that the event has never carried.
   */
  const trackEnableStarted = useCallback(
    () => track('integration', 'integrationEnableStarted', enableStartedPayload('Re-Enabled', LIST_SOURCE)),
    [track],
  );

  const trackDisableStarted = useCallback(
    (endpointId: string, { isLastEnabled }: { isLastEnabled: boolean }) =>
      track('integration', 'integrationDisableStarted', disableStartedPayload(endpointId, isLastEnabled, LIST_SOURCE)),
    [track],
  );

  const trackDeleteStarted = useCallback(
    (endpointId: string, { isOnlyWebhook }: { isOnlyWebhook: boolean }) =>
      track('integration', 'integrationDisableStarted', {
        'Integration Name': INTEGRATION_NAME,
        'Integration Category': INTEGRATION_CATEGORY,
        'Webhook Endpoint ID': endpointId,
        'Webhook Endpoint Disabled or Deleted': 'Deleted',
        ...(isOnlyWebhook ? { 'Integration Disabled or Deleted': 'Deleted' } : {}),
        Source: LIST_SOURCE,
      }),
    [track],
  );

  return useMemo(
    () => ({ trackEditStarted, trackKeyCopied, trackEnableStarted, trackDisableStarted, trackDeleteStarted }),
    [trackEditStarted, trackKeyCopied, trackEnableStarted, trackDisableStarted, trackDeleteStarted],
  );
}

/**
 * Create/edit form analytics. Same events as the list, reported against the form surface, plus the
 * one event only the create form sends.
 *
 * The enable/disable pair fires from the state toggle itself rather than from the save, so a user
 * who flips the toggle and then abandons the page is still counted — the events record the intent,
 * not the outcome.
 */
export function useReportingWebhookFormAnalytics() {
  const { track } = useAnalytics();

  const trackKeyCopied = useCallback(
    (endpointId: string) =>
      track('integration', 'reportingWebhookKeyCopied', {
        'Webhook Endpoint ID': endpointId,
        Source: FORM_SOURCE,
      }),
    [track],
  );

  const trackEnableStarted = useCallback(
    ({ isNewWebhook }: { isNewWebhook: boolean }) =>
      track(
        'integration',
        'integrationEnableStarted',
        enableStartedPayload(isNewWebhook ? 'Enabled' : 'Re-Enabled', FORM_SOURCE),
      ),
    [track],
  );

  const trackDisableStarted = useCallback(
    (endpointId: string, { isLastEnabled }: { isLastEnabled: boolean }) =>
      track('integration', 'integrationDisableStarted', disableStartedPayload(endpointId, isLastEnabled, FORM_SOURCE)),
    [track],
  );

  /**
   * Both outcomes of a "Send test". On create there is no webhook yet, so the endpoint id is
   * empty — the event still fires, because the test itself is the thing being measured.
   */
  const trackTestSent = useCallback(
    (endpointId: string, { passed, response }: { passed: boolean; response?: string }) =>
      track('integration', 'reportingWebhookTestSent', {
        'Test Passed': passed,
        'Webhook Endpoint ID': endpointId,
        ...(response == null ? {} : { 'Test Response': response }),
      }),
    [track],
  );

  /**
   * Fired when the create form opens, not when it saves — the workspace is only recorded as
   * turning reporting webhooks on for the very first one, so a second webhook sends the event
   * with the transition property left off entirely. There is no endpoint to name yet, and no
   * source: this is the one Enable Started that identifies neither.
   */
  const trackCreateOpened = useCallback(
    ({ isFirstWebhook }: { isFirstWebhook: boolean }) =>
      track('integration', 'integrationEnableStarted', {
        'Integration Name': INTEGRATION_NAME,
        'Integration Category': INTEGRATION_CATEGORY,
        ...(isFirstWebhook ? { 'Integration Enabled or Re-Enabled': 'Enabled' } : {}),
      }),
    [track],
  );

  return useMemo(
    () => ({ trackKeyCopied, trackEnableStarted, trackDisableStarted, trackTestSent, trackCreateOpened }),
    [trackKeyCopied, trackEnableStarted, trackDisableStarted, trackTestSent, trackCreateOpened],
  );
}
